import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Snackbar, Typography } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import dayjs from 'dayjs';
import { PageHeaderBar } from '../../components/layout/PageHeaderBar';
import { appShellHeaderTitleSx } from '../../components/layout/layoutConstants';
import withComponentAuth from '../../hoc/withComponentAuth';
import { dashboardTokens } from '../Dashboard/Components/dashboardCardStyles';
import { DateRangePopover } from './components/DateRangePopover';
import { NotificationBell } from './components/NotificationBell';
import { AdminAvatar } from './components/AdminAvatar';
import { SummaryCards } from './components/SummaryCards';
import { PlatformCard } from './components/PlatformCard';
import { LeadsTable } from './components/LeadsTable';
import {
  IntegrationDialog,
  IntegrationDialogMode,
} from './components/IntegrationDialog';
import { MetaSetupDialog } from './components/MetaSetupDialog';
import {
  AdPlatform,
  Campaign,
  IntegrationStatusResponse,
  LeadSummaryResponse,
  connectIntegration,
  disconnectIntegration,
  getIntegrationStatus,
  getLeadSummary,
  getMarketingLeadCampaigns,
  getMarketingLeads,
  syncIntegrationNow,
} from '../../services/marketingLeads';

const DATE_FORMAT = 'YYYY-MM-DD';

const defaultRange = () => ({
  from: dayjs().subtract(29, 'day').format(DATE_FORMAT),
  to: dayjs().format(DATE_FORMAT),
});

const escapeCsvCell = (value: string) => `"${value.replace(/"/g, '""')}"`;

const LeadsManagement = () => {
  const [range, setRange] = useState(defaultRange);
  const [summary, setSummary] = useState<LeadSummaryResponse | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [platformFilter, setPlatformFilter] = useState<AdPlatform | ''>('');
  const [exporting, setExporting] = useState(false);
  const [integrations, setIntegrations] = useState<IntegrationStatusResponse | null>(null);
  const [connectingPlatform, setConnectingPlatform] = useState<'META' | 'GOOGLE' | null>(null);
  const [syncingPlatform, setSyncingPlatform] = useState<'META' | 'GOOGLE' | null>(null);
  const [disconnectingPlatform, setDisconnectingPlatform] = useState<
    'META' | 'GOOGLE' | null
  >(null);
  const [dialog, setDialog] = useState<{
    open: boolean;
    mode: IntegrationDialogMode;
    platform: 'META' | 'GOOGLE';
  }>({ open: false, mode: 'connect', platform: 'META' });
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [integrationToast, setIntegrationToast] = useState<'success' | 'error' | 'setup' | null>(null);
  const [metaSetupOpen, setMetaSetupOpen] = useState(false);
  const [leadsRefreshKey, setLeadsRefreshKey] = useState(0);
  const [syncMessage, setSyncMessage] = useState<{ severity: 'success' | 'error'; text: string } | null>(null);
  const integrationOutcome = searchParams.get('integration');

  const fetchSummary = useCallback(async () => {
    try {
      const result = await getLeadSummary({ from: range.from, to: range.to });
      setSummary(result);
    } catch (error) {
      console.error('Failed to load lead summary:', error);
      setSummary(null);
    }
  }, [range]);

  const fetchIntegrations = useCallback(async () => {
    try {
      const result = await getIntegrationStatus();
      setIntegrations(result);
    } catch (error) {
      console.error('Failed to load integration status:', error);
      setIntegrations(null);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchIntegrations();
  }, [fetchIntegrations]);

  const fetchCampaigns = useCallback(async () => {
    try {
      setCampaigns(await getMarketingLeadCampaigns());
    } catch (error) {
      console.error('Failed to load campaigns:', error);
      setCampaigns([]);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  // Everything on this page that the Meta setup dialog can change: the
  // connection card, the KPI tiles, the campaign filter list and the leads
  // table. Before this only the connection status was refetched, so a
  // history import that landed 24 leads left the page showing 0 until a
  // manual reload.
  const refreshAll = useCallback(async () => {
    await Promise.all([fetchIntegrations(), fetchSummary(), fetchCampaigns()]);
    setLeadsRefreshKey((k) => k + 1);
  }, [fetchIntegrations, fetchSummary, fetchCampaigns]);

  // Land here after the OAuth callback redirect (?integration=success|error)
  // — refresh connection status once, then strip the param so a page reload
  // doesn't re-show the toast.
  useEffect(() => {
    if (!integrationOutcome) return;
    // 'setup' = token stored but the login sees more than one ad account or
    // Page, so the admin has to choose — open the setup dialog straight away.
    if (integrationOutcome === 'setup') {
      setIntegrationToast('setup');
      setMetaSetupOpen(true);
    } else {
      setIntegrationToast(integrationOutcome === 'success' ? 'success' : 'error');
    }
    fetchIntegrations();
    const next = new URLSearchParams(searchParams);
    next.delete('integration');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationOutcome]);

  const handleConnect = async (platform: 'META' | 'GOOGLE') => {
    try {
      setConnectingPlatform(platform);
      const { url } = await connectIntegration(platform);
      window.location.href = url;
    } catch (error) {
      console.error(`Failed to start ${platform} connect flow:`, error);
      setConnectingPlatform(null);
    }
  };

  const handleSyncNow = async (platform: 'META' | 'GOOGLE') => {
    try {
      setSyncingPlatform(platform);
      await syncIntegrationNow(platform);
      await refreshAll();
      setSyncMessage({ severity: 'success', text: `${platform === 'META' ? 'Meta' : 'Google'} campaign data synced.` });
    } catch (error: any) {
      console.error(`Failed to sync ${platform}:`, error);
      // The backend returns the classified reason (expired token, server
      // misconfiguration, rate limit) — show it rather than a silent failure.
      setSyncMessage({ severity: 'error', text: error?.message || `${platform} sync failed.` });
      fetchIntegrations();
    } finally {
      setSyncingPlatform(null);
    }
  };

  const handleDisconnect = async (platform: 'META' | 'GOOGLE') => {
    try {
      setDisconnectingPlatform(platform);
      await disconnectIntegration(platform);
      await fetchIntegrations();
      setDialog((d) => ({ ...d, open: false }));
    } catch (error) {
      console.error(`Failed to disconnect ${platform}:`, error);
    } finally {
      setDisconnectingPlatform(null);
    }
  };

  const handleDialogConfirm = () => {
    if (dialog.mode === 'disconnect') {
      handleDisconnect(dialog.platform);
      return;
    }
    // The connect flow navigates away to the provider, so the dialog is
    // left open behind the redirect rather than closed first.
    handleConnect(dialog.platform);
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const result = await getMarketingLeads({
        page: 1,
        limit: 1000,
        source: platformFilter || undefined,
      });

      const header = ['Name', 'Phone', 'Email', 'Source', 'Campaign', 'Received On', 'ROAS', 'Status'];
      const rows = result.data.map((lead) => [
        lead.name,
        lead.phoneNumber,
        lead.email,
        lead.platform,
        lead.campaignName || 'Uncategorized',
        dayjs(lead.receivedOn).format('YYYY-MM-DD HH:mm'),
        lead.roas != null ? String(lead.roas) : '',
        lead.status,
      ]);

      const csv = [header, ...rows].map((row) => row.map(escapeCsvCell).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `leads-${range.from}-to-${range.to}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export leads:', error);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pb: 3 }}>
      <PageHeaderBar showDivider>
        <Box>
          <Typography sx={appShellHeaderTitleSx}>Lead Management</Typography>
          <Typography sx={{ fontSize: 12, color: dashboardTokens.textSecondary, mt: 0.25 }}>
            Overview of your lead performance
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <DateRangePopover from={range.from} to={range.to} onChange={setRange} />
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownloadOutlinedIcon />}
            disabled={exporting}
            onClick={handleExport}
            sx={{ borderColor: dashboardTokens.border, color: '#e2e8f0', textTransform: 'none', fontWeight: 600 }}
          >
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
          <NotificationBell />
          <AdminAvatar />
        </Box>
      </PageHeaderBar>

      <Box sx={{ px: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <SummaryCards
          overall={summary?.overall || null}
          range={summary?.range || null}
          currency={summary?.currency}
        />

        <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
          <PlatformCard
            platform="META"
            metrics={summary?.platforms?.META || null}
            currency={summary?.currency}
            integration={integrations?.META || null}
            connecting={connectingPlatform === 'META'}
            syncing={syncingPlatform === 'META'}
            onViewDetails={() => navigate('/leads/meta')}
            // Meta connect/setup lives in its own dialog: OAuth or a System
            // User token, then ad-account + Page selection and a health check.
            onConnect={() => setMetaSetupOpen(true)}
            onManage={() => setMetaSetupOpen(true)}
            onDisconnect={() =>
              setDialog({ open: true, mode: 'disconnect', platform: 'META' })
            }
            disconnecting={disconnectingPlatform === 'META'}
            onSyncNow={() => handleSyncNow('META')}
          />
          <PlatformCard
            platform="GOOGLE"
            metrics={summary?.platforms?.GOOGLE || null}
            currency={summary?.currency}
            integration={integrations?.GOOGLE || null}
            connecting={connectingPlatform === 'GOOGLE'}
            syncing={syncingPlatform === 'GOOGLE'}
            onViewDetails={() => navigate('/leads/google')}
            onConnect={() =>
              setDialog({ open: true, mode: 'connect', platform: 'GOOGLE' })
            }
            onDisconnect={() =>
              setDialog({ open: true, mode: 'disconnect', platform: 'GOOGLE' })
            }
            disconnecting={disconnectingPlatform === 'GOOGLE'}
            onSyncNow={() => handleSyncNow('GOOGLE')}
          />
        </Box>

        <LeadsTable
          campaigns={campaigns}
          platformFilter={platformFilter}
          onPlatformFilterChange={setPlatformFilter}
          refreshKey={leadsRefreshKey}
        />
      </Box>

      <IntegrationDialog
        open={dialog.open}
        mode={dialog.mode}
        platform={dialog.platform}
        accountId={integrations?.[dialog.platform]?.externalAccountId || null}
        busy={
          dialog.mode === 'disconnect'
            ? disconnectingPlatform === dialog.platform
            : connectingPlatform === dialog.platform
        }
        onCancel={() => setDialog((d) => ({ ...d, open: false }))}
        onConfirm={handleDialogConfirm}
      />

      <MetaSetupDialog
        open={metaSetupOpen}
        integration={integrations?.META || null}
        connecting={connectingPlatform === 'META'}
        onClose={() => setMetaSetupOpen(false)}
        onOAuthConnect={() => handleConnect('META')}
        onChanged={refreshAll}
      />

      <Snackbar
        open={Boolean(integrationToast)}
        autoHideDuration={6000}
        onClose={() => setIntegrationToast(null)}
      >
        <Alert severity={integrationToast === 'error' ? 'error' : integrationToast === 'setup' ? 'info' : 'success'} variant="filled">
          {integrationToast === 'success'
            ? 'Ad account connected successfully.'
            : integrationToast === 'setup'
              ? 'Connected. Choose the ad account and Page to finish setup.'
              : 'Could not connect the ad account. Please try again.'}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(syncMessage)}
        autoHideDuration={8000}
        onClose={() => setSyncMessage(null)}
      >
        <Alert severity={syncMessage?.severity || 'info'} variant="filled" onClose={() => setSyncMessage(null)}>
          {syncMessage?.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default withComponentAuth(LeadsManagement);
